import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import DashboardHeader from "./web/widgets/DashboardHeader";
import DashboardKpis from "./web/widgets/DashboardKpis";
import DashboardKpis2 from "./web/widgets/DashboardKpis2";
import DashboardLatestLeaves from "./web/widgets/DashboardLatestLeaves";
import DashboardTypeBreakdown from "./web/widgets/DashboardTypeBreakdown";
import DashboardVcomp from "./web/widgets/DashboardVcomp";
import DashboardLeaveTrend from "./web/widgets/DashboardLeaveTrend";
import DashboardLeaveStatusDonut from "./web/widgets/DashboardLeaveStatusDonut";
import DashboardSafeLeavesTrend from "./web/widgets/DashboardSafeLeavesTrend";
import DashboardSafeReminders from "./web/widgets/DashboardSafeReminders";
import DashboardSafeLinks from "./web/widgets/DashboardSafeLinks";
import DashboardSafeGuides from "./web/widgets/DashboardSafeGuides";
import { checkPermission, isAdmin } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";

export default function WebDashboardView() {
    const leaves = useSelector((state) => state.leaves.list);
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
    const userRoleId = useSelector((state) => state.login.userRoleId);

    const managedTeamIds = useMemo(() => {
        if (!selectedTeamsIds || !selectedTeamsIds.length) return [];
        return selectedTeamsIds.filter((teamId) =>
            checkPermission(config.scopes.leaves, config.permissionTypes.edit, teamId)
        );
    }, [selectedTeamsIds]);

    const scopedLeaves = useMemo(() => {
        if (!leaves || !leaves.length) return [];
        if (isAdmin(userRoleId)) return leaves;
        if (!managedTeamIds.length) return [];
        return leaves.filter((leave) => managedTeamIds.includes(leave.id_team));
    }, [leaves, managedTeamIds, userRoleId]);

    const leaveStats = useMemo(() => {
        const base = {
            total: 0,
            pending: 0,
            published: 0,
            validated: 0,
            deleted: 0,
            deleteRequested: 0,
        };
        if (!scopedLeaves || !scopedLeaves.length) return base;
        return scopedLeaves.reduce((acc, leave) => {
            acc.total += 1;
            switch (leave.status) {
                case "PENDING":
                    acc.pending += 1;
                    break;
                case "PUBLISHED":
                    acc.published += 1;
                    break;
                case "VALIDATED":
                    acc.validated += 1;
                    break;
                case "DELETE_REQUESTED":
                    acc.deleteRequested += 1;
                    break;
                case "DELETED":
                    acc.deleted += 1;
                    break;
                default:
                    break;
            }
            return acc;
        }, base);
    }, [scopedLeaves]);

    const typeBreakdown = useMemo(() => {
        if (!scopedLeaves || !scopedLeaves.length) return [];
        const counts = scopedLeaves.reduce((acc, leave) => {
            const type = leave.type || "Autre";
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6);
    }, [scopedLeaves]);

    const latestLeaves = useMemo(() => {
        if (!scopedLeaves || !scopedLeaves.length) return [];
        return [...scopedLeaves]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 6);
    }, [scopedLeaves]);

    const kpis2 = useMemo(() => {
        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();
        let monthlyRequests = 0;
        let validationTotalDays = 0;
        let validationCount = 0;
        let rejectedCount = 0;
        let ongoingAbsences = 0;

        (scopedLeaves || []).forEach((leave) => {
            const createdRaw = leave.created_at ?? leave.createdAt ?? leave.fromDate ?? leave.from;
            if (createdRaw) {
                const created = new Date(createdRaw);
                if (!Number.isNaN(created.getTime())) {
                    if (created.getFullYear() === year && created.getMonth() === month) {
                        monthlyRequests += 1;
                    }
                }
            }

            if (leave.status === "REJECTED") {
                rejectedCount += 1;
            }

            const fromRaw = leave.fromDate ?? leave.from;
            const toRaw = leave.toDate ?? leave.to;
            if (fromRaw && toRaw) {
                const from = new Date(fromRaw);
                const to = new Date(toRaw);
                if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
                    if (from <= now && now <= to) {
                        ongoingAbsences += 1;
                    }
                }
            }

            if (leave.status === "VALIDATED") {
                const validatedRaw = leave.validated_at ?? leave.validatedAt ?? leave.updated_at ?? leave.updatedAt;
                const createdRawForDelay = leave.created_at ?? leave.createdAt;
                if (validatedRaw && createdRawForDelay) {
                    const validated = new Date(validatedRaw);
                    const created = new Date(createdRawForDelay);
                    if (!Number.isNaN(validated.getTime()) && !Number.isNaN(created.getTime())) {
                        const diffMs = validated.getTime() - created.getTime();
                        if (diffMs >= 0) {
                            validationTotalDays += diffMs / (1000 * 60 * 60 * 24);
                            validationCount += 1;
                        }
                    }
                }
            }
        });

        const avgValidationDays = validationCount
            ? validationTotalDays / validationCount
            : 0;
        const refusalRate = scopedLeaves && scopedLeaves.length
            ? (rejectedCount / scopedLeaves.length) * 100
            : 0;

        return {
            monthlyRequests,
            avgValidationDays,
            refusalRate,
            ongoingAbsences,
        };
    }, [scopedLeaves]);

    const canViewLeaveInsights = checkPermission(
        config.scopes.leaves,
        config.permissionTypes.view,
        selectedTeamsIds && selectedTeamsIds.length ? selectedTeamsIds : null
    );


    return (
        <Box
            sx={{
                px: { xs: 2, md: 4 },
                py: { xs: 1.5, md: 2 },
                backgroundColor: "#f5f1ea",
                backgroundImage:
                    "radial-gradient(circle at 10% 10%, rgba(255, 232, 206, 0.6), transparent 50%), radial-gradient(circle at 90% 0%, rgba(207, 226, 255, 0.45), transparent 45%)",
                color: "#1b1b1f",
                fontFamily:
                    '"Space Grotesk", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                /* Fade-in on mount */
                animation: "dashFadeIn 400ms cubic-bezier(0, 0, 0.2, 1) both",
                "@keyframes dashFadeIn": {
                    from: { opacity: 0, transform: "translateY(6px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
            }}
        >
            <Stack spacing={3}>
                <DashboardHeader
                    showSummary={canViewLeaveInsights}
                />

                {canViewLeaveInsights ? (
                    <>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    lg: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.9fr)",
                                },
                                gap: 2.5,
                                alignItems: "start",
                                gridAutoRows: "min-content",
                                "& > *": {
                                    minWidth: 0,
                                    transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 8px 28px rgba(255, 144, 21, 0.08), 0 2px 8px rgba(27, 27, 31, 0.04)",
                                    },
                                },
                            }}
                        >
                            <Stack spacing={2.5}>
                                <DashboardKpis
                                    pendingLeaves={leaveStats.pending}
                                    leaveStats={leaveStats}
                                />
                                <DashboardKpis2
                                    monthlyRequests={kpis2.monthlyRequests}
                                    avgValidationDays={kpis2.avgValidationDays}
                                    refusalRate={kpis2.refusalRate}
                                    ongoingAbsences={kpis2.ongoingAbsences}
                                />
                            </Stack>
                            <DashboardLeaveTrend leaves={scopedLeaves} compact />
                            <DashboardLeaveStatusDonut leaves={scopedLeaves} />
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    lg: "minmax(0, 2fr) minmax(0, 1fr)",
                                },
                                gap: 2.5,
                                alignItems: "stretch",
                                "& > *": {
                                    minWidth: 0,
                                    transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 8px 28px rgba(255, 144, 21, 0.08), 0 2px 8px rgba(27, 27, 31, 0.04)",
                                    },
                                },
                            }}
                        >
                            <DashboardLatestLeaves
                                latestLeaves={latestLeaves}
                                leaveStats={leaveStats}
                            />
                            <DashboardTypeBreakdown
                                typeBreakdown={typeBreakdown}
                                leaveStats={leaveStats}
                            />
                        </Box>
                    </>
                ) : (
                    <Stack spacing={2.5}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                                gap: 2.5,
                                alignItems: "start",
                                gridAutoRows: "min-content",
                                "& > *": {
                                    minWidth: 0,
                                    transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 8px 28px rgba(255, 144, 21, 0.08), 0 2px 8px rgba(27, 27, 31, 0.04)",
                                    },
                                },
                            }}
                        >
                            <DashboardSafeReminders />
                            <DashboardSafeLinks />
                            <DashboardSafeGuides />
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1fr)" },
                                gap: 2.5,
                                alignItems: "stretch",
                                "& > *": {
                                    minWidth: 0,
                                    transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 8px 28px rgba(255, 144, 21, 0.08), 0 2px 8px rgba(27, 27, 31, 0.04)",
                                    },
                                },
                            }}
                        >
                            <DashboardVcomp compact showEmpty />
                            <DashboardSafeLeavesTrend />
                        </Box>
                    </Stack>
                )}

                {canViewLeaveInsights && <DashboardVcomp />}
            </Stack>
        </Box>
    );
}
